import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import CloseIcon from "@mui/icons-material/Close";
import { CustomModal } from "@/components/Modal";
import Grid from "@mui/material/Grid";
import { type Control, useForm } from "react-hook-form";
import {
  type PropertySummaryNewForm,
  PropertySummaryNewSchema,
} from "@/features/propertySummary/types/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { Button, ButtonVariantOption, SubmitButton } from "@/components/Button";
import { Label } from "@/components/Label";
import { CTextFieldPastDays } from "@/components/TextField/CTextFieldPastDays";
import { pastDaysDescription } from "@/utils/utilsAcquireBook";
import { useUserConfig } from "@/hooks/useUserConfig";
import { type AddressIntegrator } from "@/utils/AddressIntegrator";
import { CSelect } from "@/components/Select";
import type { ISelectObject } from "@/types/select";
import { type GridSelectionModel } from "@mui/x-data-grid";
import {
  type PropertySheetReuseInfo,
  type ReusePicturesAPIRequest,
  type ReusePicturesGetAPIResponse,
  useReusePicturesMutateAPI,
} from "@/features/propertySummary/api/ReusePicturesAPI";
import { useApiClient } from "@/hooks/useApiClient";
import { ChibanKaokuNumberSearchModal } from "@/components/KaokuNumberSearch/ChibanKaokuNumberSearchModal";
import { type ChibanInput, type SearchResultRow } from "@/types/kaokuNumber";
import { PropertySummaryReuseFeedGrid } from "@/features/propertySummary/components/PropertySummaryReuseBookFeedGrid";
import { toast } from "react-toastify";
import createPropertySummaryAPI, {
  type CreatePropertySheetAPIRequest,
  type ReuseAddress,
  type ReuseDetail,
} from "@/features/propertySummary/api/CreatePropertySummaryAPI";
import { type BookTypeEng, type ReuseUiData } from "@/types/acquirebook";
import { type z } from "zod";
import { type ReuseSchema } from "@/schemas/acquirebook";
import ModifyMap, {
  type ModifyMapHandle,
} from "@/features/propertySummary/components/ModifyMap";
import { type LocalPreview } from "@/features/propertySummary/components/EditModal";
import { useNavigate } from "react-router-dom";
import { useFeatureFlags } from "@/configs/featureFlag";
import {
  GetPropertySummaryAmount,
  type IPropertySummaryAmountAPIRequest,
  type IPropertySummaryAmountAPIResponse,
} from "@/features/propertySummary/api/GetPropertySummaryAmount";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { useIsMobile } from "@/hooks/useIsMobile";
import { FieldList } from "@/components/Responsive";
import { ReuseInformationRadioButtons } from "@/components/AcquireBook/ReuseInformationRadioButtons";
import { type ReuseInfoGetAPIResponse } from "@/features/acquireBook/api/ReuseInfoAPI";
import {
  RealEstateTypeLabels,
  RealEstateTypeValues,
} from "@/features/storedpictures/types/enums";

const MANUAL_LINK_URL =
  "https://trustart.notion.site/4-1d114754f2df806f9991e47fdee087bd#1d114754f2df81008d8cc6cefc792f78";

/**
 * キャメルケースで表現された文字列をスネークケースに変換する
 * Based On AI Generated Code
 * @param str キャメルケースの文字列
 * @returns スネークケースの文字列
 */
const camelToSnake = (str: string): string => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

/**
 * オブジェクトのキーをキャメルケースからスネークケースに変換する
 * Based On AI Generated Code
 * @param obj 変換対象のオブジェクト
 * @returns キーがスネークケースに変換されたオブジェクト
 */
export const convertObjectToSnakeCase = <T extends object>(obj: T): unknown => {
  if (Array.isArray(obj)) {
    return obj.map((item: unknown) =>
      typeof item === "object" && item !== null
        ? convertObjectToSnakeCase(item)
        : item
    );
  }

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      camelToSnake(key),
      typeof value === "object" && value !== null
        ? convertObjectToSnakeCase(value)
        : value,
    ])
  );
};

export const FieldLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
  ...rest
}) => {
  return (
    <Typography variant="subtitle2" component="div" mt={2} mb={1} {...rest}>
      {children}
    </Typography>
  );
};

/**
 * 中古登記の検索結果を取得方法のラジオボタン用のデータに変換する
 * （PropertySummaryReuseFeedGrid の PC 表示と同じ変換）
 * @param data 中古登記の検索結果
 * @returns ReuseUiData
 */
const toReuseUiData = (data: ReuseInfoGetAPIResponse[]): ReuseUiData => {
  const own = data.find((item) => item.reuseOption === "REUSE_OWN");
  const trustart = data.find((item) => item.reuseOption === "REUSE_TRUSTART");
  return {
    reuseNew: { disabled: false },
    reuseOwn: own?.acquiredDateReuseOwn
      ? {
          acquiredDate: own.acquiredDateReuseOwn,
          disabled: false,
          pictureRequestDetailId: own.pictureRequestDetailId,
        }
      : null,
    reuseTrustart: trustart?.acquiredDateReuseTrustart
      ? {
          acquiredDate: trustart.acquiredDateReuseTrustart,
          disabled: false,
          pictureRequestDetailId: trustart.pictureRequestDetailId,
        }
      : null,
  };
};

const realEstateTypeLabel = (value: string): string => {
  if (value === RealEstateTypeValues.BUILDING)
    return RealEstateTypeLabels.BUILDING;
  if (value === RealEstateTypeValues.CONDOMINIUM)
    return RealEstateTypeLabels.CONDOMINIUM;
  return RealEstateTypeLabels.LAND;
};

/**
 * 中古登記の検索結果の SP 表示（1件 = 1カード）。PC は PropertySummaryReuseFeedGrid（DataGrid）。
 * 取得方法のラジオは PC と同じフォーム値（pictureCheckOptionsLand / Building）に書き込む。
 * ラジオは <div> のため、値が <p> の ResponsiveTable ではなく FieldList（<dd>）で並べる
 */
export const ReuseCardList: React.FC<{
  reuse: ReusePicturesGetAPIResponse;
  control: Control<PropertySummaryNewForm>;
}> = ({ reuse, control }) => {
  const cards = [
    {
      key: "land",
      info: reuse.reuseLand,
      name: "pictureCheckOptionsLand" as const,
    },
    {
      key: "building",
      info: reuse.reuseBuilding,
      name: "pictureCheckOptionsBuilding" as const,
    },
  ];
  return (
    <Stack spacing={1.5}>
      {cards.map(({ key, info, name }) => (
        <Paper key={key} variant="outlined" sx={{ p: 2 }}>
          <FieldList
            items={[
              ["種別", realEstateTypeLabel(info.realEstateType)],
              ["都道府県", info.prefName],
              ["所在", info.locationFull],
              ["地番または家屋番号", info.chibanKaokuNumber],
              [
                "取得方法",
                <ReuseInformationRadioButtons
                  key={name}
                  name={name}
                  control={control}
                  reuseData={toReuseUiData(info.reusePictures)}
                />,
              ],
            ]}
          />
        </Paper>
      ))}
    </Stack>
  );
};

export interface NewPropertySummaryModalProps {
  open: boolean;
  handleClose: (
    event: Record<string, unknown>,
    reason: "backdropClick" | "escapeKeyDown" | null
  ) => void;
  children?: React.ReactNode;
  adi?: AddressIntegrator;
}

export const NewPropertySummaryModal: React.FC<
  NewPropertySummaryModalProps
> = ({ open, handleClose, adi }) => {
  const reactHookForm = useForm<PropertySummaryNewForm>({
    mode: "all",
    defaultValues: {
      pastDays: 0, // デフォルト値を0に設定（useUserConfigで上書きされる）
      kaokuNumbersDisplay: [],
      primaryKaokuNumber: "",
      requestClosureBook: false,
      pictureCheckOptionsLand: null,
      pictureCheckOptionsBuilding: null,
      latitude: 0,
      longitude: 0,
    },
    resolver: zodResolver(PropertySummaryNewSchema),
  });

  const isMobile = useIsMobile();

  // ローディングのState
  const [loading, setLoading] = useState<boolean>(false);

  // 確認ダイアログのState
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);

  // ステッパーの状態管理
  const [activeStep, setActiveStep] = useState<number>(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(
    new Set<number>()
  );

  // 金額を表示するためのfeature flagの取得とState
  const { displayChargePriceInRequestModal } = useFeatureFlags();
  const [isPriceHiddenFromUserConfig, setIsPriceHiddenFromUserConfig] =
    useState<boolean>(false);

  const {
    control,
    setValue,
    getValues,
    formState,
    handleSubmit,
    trigger,
    watch,
  } = reactHookForm;

  // フォームの値を監視して、変更時に再レンダリングをトリガー
  const primaryKaokuNumber = watch("primaryKaokuNumber");
  const pastDays = watch("pastDays");
  const pictureCheckOptionsLand = watch("pictureCheckOptionsLand");
  const pictureCheckOptionsBuilding = watch("pictureCheckOptionsBuilding");

  // ユーザー設定取得
  useUserConfig({
    onUserSettingsLoaded: (data) => {
      setValue("pastDays", data.pictureRequestPastDays);
      setIsPriceHiddenFromUserConfig(data.isPriceHidden);
    },
    // 一度でも入力があったら更新しないようにする
    enableCallbackOnRefetch: !formState.isDirty,
  });

  // バックエンドAPI呼び出し関係
  const { apiClient } = useApiClient();
  const isReadOnly = useReadOnlyUser();
  const reuseApiMutate = useReusePicturesMutateAPI(apiClient);
  const createPropertySummaryAPIMutate = createPropertySummaryAPI(apiClient);

  // 物件概要書の作成、全部事項を取得するの金額を取得するフックとそれを使用するためのstate
  const getPropertySummaryAmount = GetPropertySummaryAmount(apiClient);
  const [propertySummaryAmount, setPropertySummaryAmount] =
    useState<IPropertySummaryAmountAPIResponse | null>(null);

  // このStateは本来不要だけど、家屋番号検索が必要としているのでset系の関数だけ用意する
  const [, setKaokuNumberSearchModalOpen] = useState(false);

  // 家屋番号検索で利用するState
  const [chibanList, setChibanList] = useState<ChibanInput[]>([]);

  // 処理完了した時に他の画面へ遷移する
  const navigate = useNavigate();

  const [kaokuNumberRows, setKaokuNumberRows] = React.useState<
    SearchResultRow[]
  >([]);
  const [kaokuNumberSelectedIds, setKaokuNumberSelectedIds] =
    React.useState<GridSelectionModel>([]);

  // 家屋番号検索の結果の表が変更された時に呼び出されるハンドラ
  // kaokuNumberRowsとkaokuNumberSelectedIdsに最新の変更後の値が入っている
  const onKaokuNumberDataChange = (
    kaokuNumberRows: SearchResultRow[],
    kaokuNumberSelectedIds: GridSelectionModel
  ): void => {
    // 検索結果をそのまま保持する（SearchResultRow[]として）
    setKaokuNumberRows(kaokuNumberRows);
    setKaokuNumberSelectedIds(kaokuNumberSelectedIds);
  };

  const findSelectedKaokuNumbers = (
    kaokuNumberRows: SearchResultRow[],
    selectedIds: GridSelectionModel
  ): SearchResultRow[] => {
    const selectedIdsSet = new Set(selectedIds);
    return kaokuNumberRows.filter((row) => selectedIdsSet.has(row.id));
  };

  const getKaokuNumberSelectObject = (): ISelectObject[] => {
    const filteredKaokuNumberRows = findSelectedKaokuNumbers(
      kaokuNumberRows,
      kaokuNumberSelectedIds
    );

    return filteredKaokuNumberRows.map((value, index, array) => ({
      value: value.kaokuNumber,
      label: value.kaokuNumber,
    }));
  };

  // 選択されている全ての家屋番号を取得する
  const getAllSelectedKaokuNumbers = (): string[] => {
    const filteredKaokuNumberRows = findSelectedKaokuNumbers(
      kaokuNumberRows,
      kaokuNumberSelectedIds
    );
    return filteredKaokuNumberRows
      .map((row) => row.kaokuNumber)
      .filter(Boolean);
  };

  // 中古登記関係のデータを保持するState
  const [reusePicturesGetAPIResponse, setReusePicturesGetAPIResponse] =
    React.useState<ReusePicturesGetAPIResponse | null>(null);

  // 地図関係
  const modifyMapRef = useRef<ModifyMapHandle>(null);
  const [mapSlot, setMapSlot] = useState<LocalPreview | null>(null);

  /**
   * SearchResultRow の配列を CreatePropertySummaryAPI.ts で使う ReuseAddress の配列に変換する
   * - 空の kaokuNumber は除外する
   * - reusePicture は要件により常に null を設定する
   *
   * @param rows SearchResultRow[] | undefined | null
   * @param primaryKaokuNumber string | undefined primaryKaokuNumberと同じ値は結果から取り除く
   * @returns ReuseAddress[]
   */
  const convertKaokuNumbers = (
    rows: SearchResultRow[] | undefined | null,
    primaryKaokuNumber?: string
  ): ReuseAddress[] => {
    if (!rows || rows.length === 0) return [];
    // チェックボックスにチェックを入れている内容だけが対象
    const selectedRows = rows.filter((row) => row.selected);

    let result: ReuseAddress[] = selectedRows.map((r) => {
      const prefName = r.prefectureName?.trim() ?? "";
      const locationFull = r.location?.trim() ?? "";
      const chibanKaokuNumber = r.kaokuNumber?.trim() ?? "";
      // bookType は必要ないが念のため既定値を確保（未使用）
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const _bookType =
        (r as unknown as { bookType?: BookTypeEng }).bookType ?? "LAND";

      return {
        prefName,
        locationFull,
        chibanKaokuNumber,
        reusePicture: null,
      };
    });

    // ""の家屋番号を除外
    result = result.filter((item) => item.chibanKaokuNumber !== "");

    // primaryKaokuNumberと同一の家屋番号は取り除く
    if (primaryKaokuNumber && primaryKaokuNumber !== "") {
      result = result.filter(
        (item) => item.chibanKaokuNumber !== primaryKaokuNumber
      );
    }

    return result;
  };

  /**
   * 指定されたオプションと再利用情報に基づき、画像再利用の詳細を判定して返します。
   *
   * @param {z.infer<typeof ReuseSchema> | null} pictureCheckOptions - 画像再利用の判定基準を示す値で
   *  null, "REUSE_NEW", "REUSE_OWN", "REUSE_TRUSTART"のいずれか
   * @param {PropertySheetReuseInfo | undefined} reuse - 利用可能な画像情報を含む再利用データ。
   * @returns {ReuseDetail | null} 再利用する画像のID（該当する場合）と、新規画像を要求するかどうかを含む再利用詳細オブジェクト。
   *  pictureCheckOptionsがnullまたは、reuseの長さが0の場合nullを返す
   */
  const convertReuseDetail = (
    pictureCheckOptions: z.infer<typeof ReuseSchema> | null,
    reuse: PropertySheetReuseInfo | undefined
  ): ReuseDetail | null => {
    const reusePictures = reuse?.reusePictures;
    if (!reusePictures || reusePictures.length === 0) {
      return null;
    }

    switch (pictureCheckOptions) {
      case "REUSE_NEW":
        return { reuseDetailId: null, getNewPicture: true };
      case "REUSE_OWN":
        return {
          reuseDetailId:
            reusePictures.find((item) => item.acquiredDateReuseOwn)
              ?.pictureRequestDetailId ?? null,
          getNewPicture: false,
        };
      case "REUSE_TRUSTART":
        return {
          reuseDetailId:
            reusePictures.find((item) => item.acquiredDateReuseTrustart)
              ?.pictureRequestDetailId ?? null,
          getNewPicture: false,
        };
    }
    return null;
  };

  // 家屋番号検索を実行するためのref
  const handleClickSubmitLandSearchButtonRef = useRef<
    ((cancelLoading: () => void) => void) | undefined
  >();

  const handleKaokuNumberSearch = (cancelLoading: () => void): void => {
    // handleClickSubmitLandSearchButtonRefが存在している場合は家屋番号検索を実行する
    handleClickSubmitLandSearchButtonRef.current?.(cancelLoading);
  };

  // ステップのラベル定義
  const steps = [
    "地図範囲の指定",
    "家屋番号の検索・選定",
    "登記の検索",
    "確認",
  ];

  // モーダルのスクロール制御用ref
  const modalRef = useRef<HTMLDivElement>(null);

  // ステップ変更時にモーダルを一番上にスクロール
  useEffect(() => {
    if (open && modalRef.current) {
      modalRef.current.scrollTop = 0;
    }
  }, [activeStep, open]);

  // ステップ遷移のハンドラ
  const handleNext = (): void => {
    const newCompletedSteps = new Set(completedSteps);
    newCompletedSteps.add(activeStep);
    setCompletedSteps(newCompletedSteps);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = (): void => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // 地図の表示範囲を画像にして確定する
  const handleDecideMap = (): void => {
    (async () => {
      if (modifyMapRef.current) {
        const file = await modifyMapRef.current.genImage();
        if (file) {
          setMapSlot((prev) => {
            if (prev) {
              if (prev.file) URL.revokeObjectURL(prev.url);
            }
            return {
              file,
              url: URL.createObjectURL(file),
            };
          });
        }
      }
    })();
  };

  // 各ステップのバリデーション
  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 0:
        // ステップ1: 地図範囲の画像が指定されていたら
        return mapSlot?.file !== null && mapSlot?.file !== undefined;
      case 1:
        // ステップ2: 代表家屋番号の値が入っていたら
        return primaryKaokuNumber !== "" && primaryKaokuNumber !== undefined;
      case 2: {
        // ステップ3: 経過日数の値または中古登記の指定がされていたら
        const hasPastDays =
          pastDays !== null && pastDays !== undefined && pastDays >= 0;
        const hasPictureCheckOptionsLand = pictureCheckOptionsLand !== null;
        const hasPictureCheckOptionsBuilding =
          pictureCheckOptionsBuilding !== null;
        const hasReusePicturesResponse = reusePicturesGetAPIResponse !== null;
        return (
          hasPastDays ||
          hasPictureCheckOptionsLand ||
          hasPictureCheckOptionsBuilding ||
          hasReusePicturesResponse
        );
      }
      case 3: {
        // ステップ4: step1~3の全てがOKなら
        const step0Valid =
          mapSlot?.file !== null && mapSlot?.file !== undefined;
        const step1Valid =
          primaryKaokuNumber !== "" && primaryKaokuNumber !== undefined;
        const step2HasPastDays =
          pastDays !== null && pastDays !== undefined && pastDays >= 0;
        const step2HasPictureCheckOptionsLand =
          pictureCheckOptionsLand !== null;
        const step2HasPictureCheckOptionsBuilding =
          pictureCheckOptionsBuilding !== null;
        const step2HasReusePicturesResponse =
          reusePicturesGetAPIResponse !== null;
        const step2Valid =
          step2HasPastDays ||
          step2HasPictureCheckOptionsLand ||
          step2HasPictureCheckOptionsBuilding ||
          step2HasReusePicturesResponse;
        return step0Valid && step1Valid && step2Valid;
      }
      default:
        return false;
    }
  };

  // モーダル内の全てのステートをリセット
  const resetModalState = (): void => {
    setMapSlot((prevState) => {
      if (prevState?.url) {
        URL.revokeObjectURL(prevState.url);
      }
      return null;
    });
    setActiveStep(0);
    setCompletedSteps(new Set<number>());
    setKaokuNumberRows([]);
    setKaokuNumberSelectedIds([]);
    setReusePicturesGetAPIResponse(null);
    setLoading(false);
    setConfirmDialogOpen(false);
    reactHookForm.reset();
  };

  // 物件概要書の生成の金額取得処理
  const fetchPropertySummaryAmount = useCallback(async (): Promise<void> => {
    setPropertySummaryAmount(null);
    const values = reactHookForm.getValues();

    const landReuseDetail = convertReuseDetail(
      values.pictureCheckOptionsLand,
      reusePicturesGetAPIResponse?.reuseLand
    );
    const buildingReuseDetail = convertReuseDetail(
      values.pictureCheckOptionsBuilding,
      reusePicturesGetAPIResponse?.reuseBuilding
    );

    const requestParams: IPropertySummaryAmountAPIRequest = {
      address: adi?.address ?? "",
      landPrefectureName: adi?.prefName ?? "",
      landLocationFull: adi?.buildLocationFull() ?? "",
      landChiban: adi?.chiban ?? "",
      hasLandReuse: landReuseDetail !== null,
      landReuseDetailId: landReuseDetail?.reuseDetailId ?? null,
      landReuseGetNewPicture: landReuseDetail?.getNewPicture ?? false,
      buildingPrefectureName: adi?.prefName ?? "",
      buildingLocationFull: adi?.buildLocationFull() ?? "",
      buildingPrimaryKaokuNumber: values.primaryKaokuNumber,
      hasBuildingReuse: buildingReuseDetail !== null,
      buildingReuseDetailId: buildingReuseDetail?.reuseDetailId ?? null,
      buildingReuseGetNewPicture: buildingReuseDetail?.getNewPicture ?? false,
      pastDays: values.pastDays,
    };

    const result = await getPropertySummaryAmount.trigger(requestParams);
    if (result) {
      setPropertySummaryAmount(result);
    } else {
      toast.error("物件概要書の生成に必要な金額の取得に失敗しました");
    }
  }, [
    reactHookForm,
    reusePicturesGetAPIResponse,
    kaokuNumberRows,
    adi,
    getPropertySummaryAmount,
  ]);

  // 物件概要書の生成処理
  const handleGeneratePropertySummary = async (): Promise<void> => {
    await handleSubmit(
      (values) => {
        // 中古登記データの生成
        const landReuseDetail = convertReuseDetail(
          values.pictureCheckOptionsLand,
          reusePicturesGetAPIResponse?.reuseLand
        );
        const buildingReuseDetail = convertReuseDetail(
          values.pictureCheckOptionsBuilding,
          reusePicturesGetAPIResponse?.reuseBuilding
        );

        // 家屋番号配列の生成
        const kaokuNumbers = convertKaokuNumbers(
          kaokuNumberRows,
          values.primaryKaokuNumber
        );

        // メインリクエスト生成
        const propertyData: CreatePropertySheetAPIRequest = {
          address: adi?.address ?? "",
          chiban: {
            prefName: adi?.prefName ?? "",
            locationFull: adi?.buildLocationFull() ?? "",
            chibanKaokuNumber: adi?.chiban ?? "",
            reusePicture: landReuseDetail,
          },
          primaryKaokuNumber: {
            prefName: adi?.prefName ?? "",
            locationFull: adi?.buildLocationFull() ?? "",
            chibanKaokuNumber: values.primaryKaokuNumber,
            reusePicture: buildingReuseDetail,
          },
          kaokuNumbers,
          latitude: values.latitude,
          longitude: values.longitude,
          pastDays: values.pastDays,
        };

        // スネークケースに変換
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const snake_cased_property_data =
          convertObjectToSnakeCase(propertyData);

        // フォーム形式に変換
        const formData = new FormData();
        formData.append(
          "property_data",
          JSON.stringify(snake_cased_property_data)
        );
        formData.append("map_photo", values.mapImage);
        // 解析オプション（デフォルトで全て解析）
        formData.append("include_title_section", "true");
        formData.append("include_koku", "true");
        formData.append("include_otsuku", "true");
        formData.append("analyze_method_type", "rule_based");

        // 送信
        (async () => {
          const result = await createPropertySummaryAPIMutate.trigger(formData);
          if (result && result.status === "OK") {
            toast.success("物件概要書の生成を開始しました");
            // /propertysummaryに遷移する
            navigate("/propertysummary");
          } else {
            toast.error("物件概要書の生成に失敗しました");
          }
        })();
      },
      (errors, event) => {
        if (errors.mapImage) {
          toast.error(errors.mapImage.message);
        }
      }
    )();
  };

  // モーダルが閉じられた時にステップをリセット
  const handleModalClose = (
    event: Record<string, unknown>,
    reason: "backdropClick" | "escapeKeyDown" | null
  ): void => {
    // ダイアログ外クリック、またはエスケープキー押下でダイアログを閉じない
    // これにより、意図しない状態リセットを防ぐ
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    resetModalState();
    handleClose(event, reason);
  };

  // キャンセルボタンがクリックされた時
  const handleCancel = (): void => {
    resetModalState();
    handleClose({}, null);
  };

  // adiに住所、地番等の情報が格納されている
  // このuseEffectではRHFやStateにadiの値をコピーしている
  useEffect(() => {
    // 座標をRHFに設定
    const latlng = adi?.latlng;
    if (latlng?.lat && latlng.lng) {
      reactHookForm.setValue("latitude", latlng.lat);
      reactHookForm.setValue("longitude", latlng.lng);
    }

    // 家屋番号検索のための地番リストを設定
    if (adi === undefined || !adi.prefName || !adi.chiban) {
      return;
    }

    setChibanList([
      {
        prefectureName: adi.prefName,
        location: adi.buildLocationFull(),
        chiban: adi.chiban,
        bookType: "LAND",
      },
    ]);
  }, [adi]);

  useEffect(() => {
    // 確認ステップ
    if (activeStep === 3) {
      // 全フィールドのバリデーションをトリガー
      void trigger();

      // 金額の取得
      void fetchPropertySummaryAmount();
    }
  }, [activeStep, trigger]);

  // map画像が生成されたら、react hook formにコピーする
  useEffect(() => {
    if (mapSlot?.file) {
      setValue("mapImage", mapSlot?.file, {
        shouldValidate: true,
        shouldDirty: true,
      });
      // バリデーションを明示的にトリガーしてisValidを更新
      void trigger("mapImage");
    }
  }, [mapSlot, setValue, trigger]);

  // コンポーネント削除時に画像データを破棄する
  useEffect(() => {
    return () => {
      if (mapSlot?.url) {
        URL.revokeObjectURL(mapSlot.url);
        mapSlot.file = null;
      }
    };
  }, []);

  return (
    <CustomModal
      ref={modalRef}
      isOpen={open}
      handleClose={handleModalClose}
      ariaLabelledby={"edit-modal-title"}
      ariaDescribedby={"edit-modal-description"}
      overflow="auto"
      width="80%"
      // SP は全画面にするため高さの上限を外す
      maxHeight={isMobile ? "100%" : "80vh"}
      fullScreenOnMobile
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          mb: 2,
        }}
      >
        <Typography id="property-edit-modal-title" variant="h6" component="h2">
          物件概要書作成
        </Typography>
        {/* 共通 Button は SP で幅100%になるため、内容幅で包む */}
        <Box sx={{ width: "fit-content", flexShrink: 0 }}>
          <Button
            label={"閉じる"}
            onClick={handleCancel}
            variant={ButtonVariantOption.Outlined}
            size={SizingWrapperStyle.SMALL}
            startIcon={<CloseIcon />}
          />
        </Box>
      </Box>
      <Box id="property-edit-modal-description" sx={{ my: 2 }}>
        {/* 住居表示と地番は常に上部に表示 */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12}>
            <Typography variant="body2" mb={1}>
              住居表示: {adi?.address ?? ""}
            </Typography>
            <Typography variant="body2" mb={2}>
              地番: {adi?.addressChiban ?? ""}
            </Typography>
          </Grid>
        </Grid>
        {/* SP は横並びの Stepper だとステップ名が1文字ずつ折り返すため「n / 4 ステップ名」で出す */}
        {isMobile ? (
          <Typography
            variant="subtitle1"
            component="p"
            sx={{ fontWeight: 700, mb: 2 }}
          >
            <Box component="span" sx={{ color: "primary.main", mr: 1 }}>
              {activeStep + 1} / {steps.length}
            </Box>
            {steps[activeStep]}
          </Typography>
        ) : (
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label, index) => (
              <Step key={label} completed={completedSteps.has(index)}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
        {/* 各ステップの説明 */}
        <Box sx={{ mb: 3 }}>
          {activeStep === 0 && (
            <Typography variant="body1" color="text.secondary">
              物件概要書に表示する地図の範囲を指定してください。地図を操作して表示したい範囲を調整し、「決定」ボタンをクリックして確定してください。
            </Typography>
          )}
          {activeStep === 1 && (
            <Typography variant="body1" color="text.secondary">
              対象物件の家屋番号を検索し、全部事項取得・データ化の対象とする家屋番号を選択してください。「家屋番号を検索」ボタンをクリックして検索を実行し、検索結果から対象家屋番号を選択してください。
            </Typography>
          )}
          {activeStep === 2 && (
            <Typography variant="body1" color="text.secondary">
              登記の取得方法を選択してください。経過日数を設定して自動再利用を有効にするか、または中古登記を検索して手動で選択することができます。
            </Typography>
          )}
          {activeStep === 3 && (
            <Typography variant="body1" color="text.secondary">
              入力内容を確認してください。問題がなければ「生成する」ボタンをクリックして物件概要書の生成を開始します。
            </Typography>
          )}
        </Box>
        {/* ステップ0: 地図範囲の指定 */}
        {activeStep === 0 && adi?.latlng && isMobile && (
          // SP は「操作する地図 → 決定 → 表示される地図」を縦に並べ、地図は画面幅に収める
          <Stack spacing={2} alignItems="center" mt={2}>
            <Typography variant="subtitle2">
              ここを操作して物件概要書に表示したい地図を設定してください
            </Typography>
            <Box sx={{ width: "100%", maxWidth: "300px" }}>
              <ModifyMap
                ref={modifyMapRef}
                initialPosition={adi.latlng}
                zoom={16}
                isEditable={false}
                width="100%"
                style={{ border: "1px solid #ccc", aspectRatio: "1 / 1" }}
              />
            </Box>
            <Box sx={{ width: "fit-content" }}>
              <Button
                label={"決定"}
                onClick={handleDecideMap}
                size={SizingWrapperStyle.SMALL}
                variant={ButtonVariantOption.Outlined}
              />
            </Box>
            <Typography variant="subtitle2">
              物件概要書に表示される地図
            </Typography>
            {mapSlot?.file ? (
              <img
                src={mapSlot.url}
                alt="キャプチャした地図"
                style={{
                  maxWidth: "min(300px, 100%)",
                  border: "1px solid #ccc",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "300px",
                  aspectRatio: "1 / 1",
                  border: "1px solid #ccc",
                  boxSizing: "border-box",
                }}
              />
            )}
          </Stack>
        )}
        {activeStep === 0 && !isMobile && (
          <Grid container spacing={2} mt={2}>
            <Grid item xs={12}>
              {adi?.latlng && (
                <Grid
                  container
                  mt={1}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <Grid item xs>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <Typography variant="subtitle2" maxWidth={"300px"}>
                        ここを操作して物件概要書に表示したい地図を設定してください
                      </Typography>
                      <ModifyMap
                        ref={modifyMapRef}
                        initialPosition={adi.latlng}
                        zoom={16}
                        isEditable={false}
                        width="300px"
                        height="300px"
                        style={{ border: "1px solid #ccc" }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={2}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <Button
                        label={"決定"}
                        onClick={handleDecideMap}
                        size={SizingWrapperStyle.INHERIT}
                        variant={ButtonVariantOption.Outlined}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <Typography variant="subtitle2">
                        物件概要書に表示される地図
                      </Typography>
                      {mapSlot?.file && (
                        <img
                          src={mapSlot.url}
                          alt="キャプチャした地図"
                          style={{
                            maxWidth: "300px",
                            maxHeight: "300px",
                            border: "1px solid #ccc",
                          }}
                        />
                      )}
                      {!mapSlot?.file && (
                        <Box
                          style={{
                            width: "300px",
                            height: "300px",
                            border: "1px solid #ccc",
                          }}
                        />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        )}

        {/* ステップ1: 家屋番号の検索・選定 */}
        {activeStep === 1 && (
          <Grid container spacing={isMobile ? 2 : 3} mt={2}>
            {/* セクション1: 家屋番号の検索 */}
            <Grid item xs={12}>
              <Box
                sx={{
                  border: "1px solid #E0E0E0",
                  borderRadius: "4px",
                  p: 2,
                }}
              >
                {/* 共通 Button は SP で幅100%になるため、内容幅で包む */}
                <Box sx={{ mb: 2, width: "fit-content" }}>
                  <SubmitButton
                    variant={ButtonVariantOption.Contained}
                    onClick={(_event, cancelLoading) => {
                      handleKaokuNumberSearch(() => {
                        setLoading(false);
                        cancelLoading();
                      });
                    }}
                    label={"家屋番号を検索"}
                    timeout={30000}
                    disabled={loading}
                  />
                </Box>
                {chibanList.length > 0 && (
                  <ChibanKaokuNumberSearchModal
                    // このコンポーネントはchibanListの変更にうまく追従してくれない
                    // 最初の一度しかchibanListを読み取っていない
                    // そのため地番リストが更新されたらこのコンポーネントを強制的に再マウントさせることで問題を回避する
                    // activeStepもkeyに含めることで、ステップ切り替え時に再マウントされ、初期値が正しく設定される
                    key={`${activeStep}-${JSON.stringify(chibanList)}`}
                    chibanList={chibanList}
                    hideChibanList={true}
                    hideSearchTab={true}
                    hideListButton={true}
                    hideCloseButton={true}
                    hideTitle={true}
                    handleClickSubmitLandSearchButtonRef={
                      handleClickSubmitLandSearchButtonRef
                    }
                    setKaokuNumberSearchModalIsOpen={
                      setKaokuNumberSearchModalOpen
                    }
                    appendPropertySelectionRows={() => undefined} // 使わないので空関数を指定
                    variant={"map"}
                    onKaokuNumberDataChange={onKaokuNumberDataChange}
                    initialKaokuNumberRows={
                      kaokuNumberRows.length > 0 ? kaokuNumberRows : undefined
                    }
                    initialSelectedIds={
                      kaokuNumberSelectedIds.length > 0
                        ? kaokuNumberSelectedIds
                        : undefined
                    }
                  />
                )}
              </Box>
            </Grid>
            {/* セクション2: 代表家屋番号の選定 */}
            <Grid item xs={12}>
              <Box
                sx={{
                  border: "1px solid #E0E0E0",
                  borderRadius: "4px",
                  p: 2,
                }}
              >
                <FieldLabel>代表家屋番号</FieldLabel>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  物件概要書の建物情報（構造、種類など）を取得するために、代表となる家屋番号を選択してください。
                  <br />
                  選択した家屋番号の全部事項が取得・解析され、物件概要書に反映されます。
                  <br />
                  他の家屋番号は家屋番号としてのみ記載されます。
                </Typography>
                <Box>
                  <CSelect
                    control={control}
                    name="primaryKaokuNumber"
                    label="家屋番号"
                    options={getKaokuNumberSelectObject()}
                    isClearable={false}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}

        {/* ステップ2: 登記の検索 */}
        {activeStep === 2 && (
          <Grid container spacing={isMobile ? 2 : 3} mt={2}>
            {/* セクション1: 自動再利用設定（経過日数） */}
            <Grid item xs={12}>
              <Box
                sx={{
                  border: "1px solid #E0E0E0",
                  borderRadius: "4px",
                  p: 2,
                }}
              >
                <FieldLabel>自動再利用設定</FieldLabel>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  中古登記検索を使用しない場合、この経過日数以内に取得された中古登記があれば自動的に再利用されます。
                  <br />
                  該当する登記がない場合は、新規に登記を取得します。
                </Typography>
                <Box>
                  <Label required={false}>経過日数</Label>
                  <CTextFieldPastDays
                    name="pastDays"
                    control={control}
                    tooltipText={pastDaysDescription}
                    size={SizingWrapperStyle.LARGE}
                  />
                </Box>
              </Box>
            </Grid>

            {/* セクション2: 手動検索・選択（中古登記検索） */}
            <Grid item xs={12}>
              <Box
                sx={{
                  border: "1px solid #E0E0E0",
                  borderRadius: "4px",
                  p: 2,
                }}
              >
                <FieldLabel>中古登記の手動検索・選択</FieldLabel>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  事前に中古利用可能な登記を検索し、どの登記を利用するか、または新規で取得するかを選択できます。
                  <br />
                  この機能の検索には経過日数の設定は影響しません。
                </Typography>
                {/* 共通 Button は SP で幅100%になるため、内容幅で包む */}
                <Box mb={2} sx={{ width: "fit-content" }}>
                  <Button
                    variant={ButtonVariantOption.Contained}
                    label={"中古登記を検索"}
                    onClick={() => {
                      (async () => {
                        // reusePicturesRequestを生成する
                        const reusePicturesRequest: ReusePicturesAPIRequest = {
                          land: {
                            prefName: adi?.prefName ?? "",
                            locationFull: adi?.buildLocationFull() ?? "",
                            chibanKaokuNumber: adi?.chiban ?? "",
                          },
                          building: {
                            prefName: adi?.prefName ?? "",
                            locationFull: adi?.buildLocationFull() ?? "",
                            chibanKaokuNumber:
                              getValues("primaryKaokuNumber") ?? "",
                          },
                        };

                        const result = await reuseApiMutate.trigger(
                          reusePicturesRequest
                        );
                        setReusePicturesGetAPIResponse(result);
                      })();
                    }}
                  />
                </Box>
                {isMobile ? (
                  reusePicturesGetAPIResponse && (
                    <ReuseCardList
                      reuse={reusePicturesGetAPIResponse}
                      control={control}
                    />
                  )
                ) : (
                  <PropertySummaryReuseFeedGrid
                    filterCondition={reusePicturesGetAPIResponse}
                    control={control}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        )}

        {/* ステップ3: 確認 */}
        {activeStep === 3 && (
          <Grid container spacing={2} mt={2}>
            <Grid item xs={12}>
              <Typography variant="h6" mb={2}>
                入力内容の確認
              </Typography>
              <Box mb={2}>
                <Typography variant="body2" mb={1}>
                  <strong>住居表示:</strong> {adi?.address ?? ""}
                </Typography>
                <Typography variant="body2" mb={1}>
                  <strong>地番:</strong> {adi?.addressChiban ?? ""}
                </Typography>
                <Typography variant="body2" mb={1}>
                  <strong>登記取得対象家屋番号:</strong>{" "}
                  {getValues("primaryKaokuNumber") ?? ""}
                </Typography>
                {getAllSelectedKaokuNumbers().length > 0 && (
                  <Typography variant="body2" mb={1}>
                    <strong>選択されている家屋番号:</strong>{" "}
                    {getAllSelectedKaokuNumbers().join(", ")}
                  </Typography>
                )}
                {mapSlot?.file && (
                  <Box mb={2}>
                    <Typography variant="body2" mb={1}>
                      <strong>地図:</strong>
                    </Typography>
                    <img
                      src={mapSlot.url}
                      alt="選択した地図範囲"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "400px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
      {/* ステップナビゲーションボタン */}
      {/* 共通 Button は SP で幅100%になるため、各ボタンを内容幅で包む */}
      <Stack
        id={"property-edit-modal-action"}
        direction={"row"}
        spacing={2}
        justifyContent={"flex-end"}
        flexWrap="wrap"
        useFlexGap
        mt={isMobile ? 3 : 6}
        mb={2}
      >
        {activeStep > 0 && (
          <Box sx={{ width: "fit-content" }}>
            <Button
              label={"前へ"}
              onClick={handleBack}
              variant={ButtonVariantOption.Outlined}
              size={SizingWrapperStyle.SMALL}
            />
          </Box>
        )}
        {activeStep < steps.length - 1 ? (
          <Box sx={{ width: "fit-content" }}>
            <Button
              label={"次へ"}
              onClick={handleNext}
              disabled={!isStepValid(activeStep)}
              variant={ButtonVariantOption.Contained}
              size={SizingWrapperStyle.SMALL}
            />
          </Box>
        ) : (
          <Box sx={{ width: "fit-content" }}>
            <ReadOnlyTooltip isReadOnly={isReadOnly}>
              <Button
                label={"生成する"}
                disabled={isReadOnly || !formState.isValid}
                onClick={() => {
                  setConfirmDialogOpen(true);
                }}
                variant={ButtonVariantOption.Contained}
                size={SizingWrapperStyle.SMALL}
              />
            </ReadOnlyTooltip>
          </Box>
        )}
      </Stack>
      {/* 確認ダイアログ */}
      <Dialog
        open={confirmDialogOpen}
        fullScreen={isMobile}
        onClose={(_event, reason) => {
          // ダイアログ外クリック、またはエスケープキー押下でダイアログを閉じない
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          setConfirmDialogOpen(false);
        }}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">確認</DialogTitle>
        <DialogContent>
          {displayChargePriceInRequestModal &&
          propertySummaryAmount != null &&
          !isPriceHiddenFromUserConfig ? (
            <DialogContentText id="confirm-dialog-description" component="div">
              <Typography>物件概要書の作成を開始します。</Typography>
              <Typography>
                作成件数に対して、合計
                {(
                  propertySummaryAmount.propertySummaryPrice +
                  propertySummaryAmount.allMattersAnalyzePrice +
                  propertySummaryAmount.allMattersPrice
                ).toLocaleString()}
                円が課金されます。
              </Typography>
              {/* SP は表にせず、項目ごとに「件数・金額」を縦に並べる */}
              {isMobile ? (
                <Box sx={{ my: 2 }}>
                  <FieldList
                    items={[
                      [
                        "物件概要書",
                        `${propertySummaryAmount.propertySummaryCount.toLocaleString()}件 / ${propertySummaryAmount.propertySummaryPrice.toLocaleString()}円`,
                      ],
                      [
                        "全部事項解析",
                        `${propertySummaryAmount.allMattersAnalyzeCount.toLocaleString()}件 / ${propertySummaryAmount.allMattersAnalyzePrice.toLocaleString()}円`,
                      ],
                      [
                        "全部事項",
                        `${propertySummaryAmount.allMattersCount.toLocaleString()}件 / ${propertySummaryAmount.allMattersPrice.toLocaleString()}円`,
                      ],
                    ]}
                  />
                </Box>
              ) : (
                <TableContainer sx={{ my: 2 }}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>物件概要書</TableCell>
                        <TableCell align="right">
                          {propertySummaryAmount.propertySummaryCount.toLocaleString()}
                          件
                        </TableCell>
                        <TableCell align="right">
                          {propertySummaryAmount.propertySummaryPrice.toLocaleString()}
                          円
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>全部事項解析</TableCell>
                        <TableCell align="right">
                          {propertySummaryAmount.allMattersAnalyzeCount.toLocaleString()}
                          件
                        </TableCell>
                        <TableCell align="right">
                          {propertySummaryAmount.allMattersAnalyzePrice.toLocaleString()}
                          円
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>全部事項</TableCell>
                        <TableCell align="right">
                          {propertySummaryAmount.allMattersCount.toLocaleString()}
                          件
                        </TableCell>
                        <TableCell align="right">
                          {propertySummaryAmount.allMattersPrice.toLocaleString()}
                          円
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              <Typography>
                ※取得済登記・リユース登記となる場合は価格が異なります。
              </Typography>
              <Typography>
                詳細は
                <Link
                  href={MANUAL_LINK_URL}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ mx: 0.5 }}
                >
                  マニュアル
                </Link>
                をご確認ください。
              </Typography>
            </DialogContentText>
          ) : (
            <DialogContentText id="confirm-dialog-description" component="div">
              <Typography>
                【課金が発生します】物件概要書を作成します。
              </Typography>
              <Typography>
                ※ 画面の切り替わりまでに時間がかかる場合があります
              </Typography>
            </DialogContentText>
          )}
        </DialogContent>
        {/* 共通 Button は SP で幅100%になるため、内容幅で包む */}
        <DialogActions sx={{ justifyContent: "center", mb: 1 }}>
          <Box sx={{ width: "fit-content" }}>
            <Button
              onClick={() => {
                setConfirmDialogOpen(false);
              }}
              variant={ButtonVariantOption.Outlined}
              size={SizingWrapperStyle.SMALL}
              label={"キャンセル"}
            />
          </Box>
          <Box sx={{ width: "fit-content" }}>
            <ReadOnlyTooltip isReadOnly={isReadOnly}>
              <Button
                onClick={() => {
                  setConfirmDialogOpen(false);
                  void handleGeneratePropertySummary();
                }}
                variant={ButtonVariantOption.Contained}
                size={SizingWrapperStyle.SMALL}
                label={"生成する"}
                disabled={isReadOnly}
              />
            </ReadOnlyTooltip>
          </Box>
        </DialogActions>
      </Dialog>
    </CustomModal>
  );
};

export default NewPropertySummaryModal;
